import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import ApprovalOutlinedIcon from '@mui/icons-material/ApprovalOutlined';
import {
  getCorporateEmployeeById,
  getCorporateIdCardByEmployeeId,
  getCorporatePassportDocumentByEmployeeId,
} from '../../api/endpoints/corporateEmployee';
import type { BillingInformation } from '../../api/types/billing';
import type { Gsa } from '../../api/types/system';
import type { Contact, Passenger } from '../../api/types/common';
import { buildBillingInformation, buildBillingInformationFromCurrentCode } from './billingInfo';
import { employeeToContact, employeeToPassenger } from './corporateApproval';

function ReadonlyField({ label, value }: { label: string; value?: string }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Typography variant="body2">{value || '—'}</Typography>
    </Grid>
  );
}

export interface ApprovalPassengerStepProps {
  employeeId: number;
  /** Yurtdışı seçim → pasaport + `citizenNo` sentinel. Varsayılan `false` (yurt içi:
   *  kimlik kartı + T.C. no) — otel/otobüs/transfer/araç kiralama hep yurt içi. */
  isInternational?: boolean;
  /** İzin verilen kurumsal bütçe — uyarı metninde gösterilir. */
  maxBudget?: number | null;
  loading: boolean;
  /** Uyarı metnindeki ürün adı: `'uçuş'` | `'otel rezervasyonu'` | `'transfer'` |
   *  `'araç kiralama'` | `'otobüs bileti'`. Varsayılan `'rezervasyon'`. */
  productLabel?: string;
  onSubmit: (passengers: Passenger[], contact: Contact, billing: BillingInformation) => void;
}

/**
 * Yolcu adımının kurumsal "Onaya Gönder" varyantı — seçim bütçe/politika dışıysa ve sepette
 * `EmployeeID` (ya da arama formunda personel) varsa FlowPage bunu normal yolcu formu yerine
 * gösterir. Yolcu bilgileri `CorporateEmployee/GetCorporateEmployeeById` (+ kimlik kartı /
 * pasaport) ile **salt-okunur** gelir. Aynı yanıttaki `GsaList`'ten bir cari firma seçilir;
 * seçilen `currentCode` `Billing/Submit`'e `{ ifCurrentCodeSet: true, taxNo: <currentCode> }`
 * olarak gider (legacy `Flight/Allocated` `#gsa` select'i + `#TaxNumberViaCurrent`). "Onaya
 * Gönder" `onSubmit`'i çağırır → flow `UpdatePassengers → Billing/Submit → (MakePrebooking)`
 * zincirini çalıştırıp yöneticinin onay listesine bırakır (legacy `*Controller.SendForApproval`).
 *
 * Kesinlikle tek yolcu — bütçe-dışı çok yolculu kurumsal rezervasyon normal forma düşer.
 */
export function ApprovalPassengerStep({
  employeeId,
  isInternational = false,
  maxBudget,
  loading,
  productLabel = 'rezervasyon',
  onSubmit,
}: ApprovalPassengerStepProps) {
  const employeeQuery = useQuery({
    queryKey: ['corporateEmployee', 'byId', employeeId],
    queryFn: () => getCorporateEmployeeById(employeeId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const idCardQuery = useQuery({
    queryKey: ['corporateEmployee', 'idCard', employeeId],
    queryFn: () => getCorporateIdCardByEmployeeId(employeeId),
    enabled: !isInternational,
    retry: false,
  });
  const passportQuery = useQuery({
    queryKey: ['corporateEmployee', 'passport', employeeId],
    queryFn: () => getCorporatePassportDocumentByEmployeeId(employeeId),
    enabled: isInternational,
    retry: false,
  });

  const employee = employeeQuery.data?.corporateEmployeeList?.[0];

  const gsaList = useMemo<Gsa[]>(
    () => (employee?.gsaList ?? []).filter((g: Gsa) => !!g?.currentCode),
    [employee]
  );

  // Cari firma seçimi — legacy `Flight/Allocated` `#gsa` select'i. Tek cari varsa otomatik seçili.
  const [gsaCurrentCode, setGsaCurrentCode] = useState('');
  useEffect(() => {
    setGsaCurrentCode(gsaList.length === 1 ? gsaList[0].currentCode ?? '' : '');
  }, [gsaList]);

  const passenger = useMemo<Passenger | null>(() => {
    if (!employee) return null;
    return employeeToPassenger(employee, {
      idCard: idCardQuery.data?.corporateIDCard?.[0],
      passport: passportQuery.data?.corporatePassportDocument?.[0],
      isInternational,
    });
  }, [employee, idCardQuery.data, passportQuery.data, isInternational]);

  if (employeeQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (employeeQuery.isError || !employee || !passenger) {
    return (
      <Alert severity="error">
        Personel bilgisi alınamadı — bu {productLabel} bütçe dışı olduğu için yolcu bilgileri personel
        kaydından gelmelidir. Lütfen yöneticinizle iletişime geçin.
      </Alert>
    );
  }

  const employeeName = `${employee.name} ${employee.surName}`.trim();
  const selectedGsa = gsaList.find((g: Gsa) => g.currentCode === gsaCurrentCode);
  const needsGsa = gsaList.length > 0 && !selectedGsa;

  const send = () => {
    const billing = selectedGsa
      ? buildBillingInformationFromCurrentCode(selectedGsa.currentCode ?? '', selectedGsa.name || employeeName)
      : buildBillingInformation(null, employeeName, undefined);
    onSubmit([passenger], employeeToContact(employee), billing);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 720 }}>
      <Alert severity="warning">
        Bu {productLabel} şirket seyahat politikanız / bütçeniz dışında
        {typeof maxBudget === 'number' ? ` (bütçe: ${maxBudget.toLocaleString('tr-TR')} TL)` : ''}. Yolcu
        bilgileri personel kaydınızdan gelir ve değiştirilemez. <strong>Onaya Gönder</strong> ile rezervasyon
        ön rezervasyon olarak tutulur ve yöneticinizin onay listesine düşer; onaylanınca rezervasyon
        listenizden ödemeye devam edebilirsiniz.
      </Alert>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Yolcu (personel kaydından)
          </Typography>
          <Grid container spacing={2}>
            <ReadonlyField label="Ad" value={passenger.firstName} />
            <ReadonlyField label="Soyad" value={passenger.lastName} />
            <ReadonlyField label="Cinsiyet" value={passenger.gender === 'Female' ? 'Kadın' : passenger.gender === 'Male' ? 'Erkek' : passenger.gender} />
            <ReadonlyField label="Doğum tarihi" value={passenger.birthDate} />
            {isInternational ? (
              <>
                <ReadonlyField label="Pasaport No" value={passenger.passportNo || undefined} />
                <ReadonlyField label="Pasaport geçerlilik" value={passenger.passportExpireDate || undefined} />
              </>
            ) : (
              <ReadonlyField label="T.C. Kimlik No" value={passenger.citizenNo || undefined} />
            )}
            <ReadonlyField label="E-posta" value={passenger.email || undefined} />
            <ReadonlyField label="Telefon" value={passenger.phone || undefined} />
          </Grid>
        </CardContent>
      </Card>

      {gsaList.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Cari Firma
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
              Fatura, seçtiğiniz carinin cari koduyla kesilir.
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              label="Cari firma"
              value={gsaCurrentCode}
              onChange={(e) => setGsaCurrentCode(e.target.value)}
              error={needsGsa}
              helperText={needsGsa ? 'Lütfen bir cari firma seçin.' : ' '}
            >
              <MenuItem value="">
                <em>Cari seçiniz</em>
              </MenuItem>
              {gsaList.map((g: Gsa) => (
                <MenuItem key={g.currentCode} value={g.currentCode}>
                  {(g.name || '—').toLocaleUpperCase('tr')} — {g.currentCode}
                </MenuItem>
              ))}
            </TextField>
          </CardContent>
        </Card>
      )}

      <Box>
        <Button
          variant="contained"
          color="warning"
          startIcon={<ApprovalOutlinedIcon />}
          disabled={loading || needsGsa}
          onClick={send}
        >
          {loading ? 'Gönderiliyor…' : 'Onaya Gönder'}
        </Button>
      </Box>
    </Box>
  );
}
